import React from 'react';

import { Tooltip } from '@components';
import { BarLineComboChart } from '@components/index';
import { escapeHtmlText } from '@components/rich-text-editor/sanitize';
import useIsTruncated from '@hooks/useIsTruncated';
import formatCurrency from '@utils/numberFormater';
import { BarLineComboChartSeries } from 'types/e-charts';

import AnalyticsCardState from './AnalyticsCardState';

const TOOLTIP_LINE_LENGTH = 28;
const BAR_WIDTH_PX = 45;
const BAR_RADIUS_PX = 2;
const MIN_SLOT_WIDTH_PX = 100;
const LEFT_AXIS_GUTTER_PX = 40;
const RIGHT_AXIS_GUTTER_PX = 40;
const PLOT_INSET_PX = LEFT_AXIS_GUTTER_PX + RIGHT_AXIS_GUTTER_PX;

const wrapTooltipLabel = (label: string): string => {
  const lines: string[] = [];
  let line = '';
  label.split(' ').forEach((word) => {
    const chunks =
      word.match(new RegExp(`.{1,${TOOLTIP_LINE_LENGTH}}`, 'g')) ?? [];
    chunks.forEach((chunk) => {
      const candidate = line ? `${line} ${chunk}` : chunk;
      if (line && candidate.length > TOOLTIP_LINE_LENGTH) {
        lines.push(line);
        line = chunk;
      } else {
        line = candidate;
      }
    });
  });
  if (line) lines.push(line);
  return lines.map(escapeHtmlText).join('<br/>');
};

interface LeadStatusChartCardProps {
  series: BarLineComboChartSeries[];
  isLoading?: boolean;
}

const ChartLegendLabel: React.FC<{ item: BarLineComboChartSeries }> = ({
  item
}) => {
  const { ref: textRef, isTruncated } = useIsTruncated<HTMLSpanElement>(
    item.name
  );

  return (
    <>
      <div
        className="flex min-w-0 max-w-full cursor-default items-start gap-1"
        data-tooltip-id={`chart-legend-${item.name}`}
      >
        <span className="mt-0.5 flex h-4 shrink-0 items-center">
          <span
            className="size-3 rounded-sm"
            style={{
              background: item.color
            }}
          />
        </span>
        <span
          ref={textRef}
          className="line-clamp-2 min-w-0 break-words text-left text-xs text-cofeeNeutral10"
        >
          {item.name}
        </span>
      </div>
      {isTruncated && (
        <Tooltip
          id={`chart-legend-${item.name}`}
          className="rounded-lg !bg-cofeeNeutral10"
        >
          <div className="max-w-xs whitespace-normal break-words px-2 py-1 text-sm text-cofeeNeutral1">
            {item.name}
          </div>
        </Tooltip>
      )}
    </>
  );
};

const LeadStatusChartCard: React.FC<LeadStatusChartCardProps> = ({
  series,
  isLoading = false
}) => {
  const tooltip = {
    trigger: 'axis',
    axisPointer: { type: 'shadow' },
    textStyle: { fontSize: 12 },
    appendToBody: true,
    confine: true,
    formatter: (params: any) => {
      const currentxAxisCategory = params.find(
        (p: any) => p?.axisValueLabel === p?.seriesName
      );
      const revenueCategory = params.find(
        (p: any) => p.seriesName === 'Revenue'
      );
      const mainLabel = wrapTooltipLabel(
        params[0]?.axisValueLabel ?? params[0]?.name ?? ''
      );
      const leftYAxisTooltipLabel = `Lead Count: ${
        currentxAxisCategory?.value ?? ''
      }`;
      const rightYAxisTooltipLabel = revenueCategory.value
        ? `Revenue: ₹ ${formatCurrency(
            (revenueCategory.value ?? 0) / 100,
            'INR'
          )}`
        : 'Revenue: 0';
      return `<div style="max-width:260px;white-space:normal;">${mainLabel}<br/>${leftYAxisTooltipLabel}<br/>${rightYAxisTooltipLabel}</div>`;
    }
  };

  const categories = series
    .filter((item) => item.name !== 'Revenue')
    .map((item) => item.name);

  const scrollContentMinWidth = `max(100%, ${
    categories.length * MIN_SLOT_WIDTH_PX + PLOT_INSET_PX
  }px)`;

  const hasData = series.some((item) => item.data.some((value) => value > 0));

  return (
    <div className="flex w-full flex-col items-center gap-6 rounded-xl border border-grey10 bg-white p-6 shadow-md">
      <div className=" mb-2 w-full text-left text-20 font-semibold text-content">
        Lead Count & Revenue By Status
      </div>
      <AnalyticsCardState
        isLoading={isLoading}
        hasData={hasData}
        emptyCardType="barLineChart"
      >
        <div className="flex w-full flex-col">
          <div className="mb-[10.5px] flex w-full justify-end">
            <ChartLegendLabel
              item={
                series.find(
                  (item) => item.name === 'Revenue'
                ) as BarLineComboChartSeries
              }
            />
          </div>
          <div className="custom-scroll w-full overflow-x-auto pb-1">
            <div
              style={{
                width: 'max-content',
                minWidth: scrollContentMinWidth
              }}
              className="flex flex-col"
            >
              <div className="relative flex w-full">
                <div className="absolute left-0 top-1/2 h-fit translate-y-1/2 -rotate-90 text-xs text-grey">
                  Lead count
                </div>
                <BarLineComboChart
                  categories={categories}
                  series={series}
                  width="100%"
                  height={236}
                  showXAxisLabels={false}
                  barWidth={BAR_WIDTH_PX}
                  barBorderRadius={BAR_RADIUS_PX}
                  rightYAxisFormatter={(value) =>
                    `₹ ${formatCurrency(value / 100, 'INR')}`
                  }
                  customOptions={{
                    tooltip
                  }}
                />
                <div className=" absolute right-0 top-1/2 h-fit -rotate-90 text-xs text-grey">
                  Revenue
                </div>
              </div>
              <div
                style={{
                  paddingLeft: LEFT_AXIS_GUTTER_PX,
                  paddingRight: RIGHT_AXIS_GUTTER_PX
                }}
                className="mx-auto mt-2 flex w-max items-start gap-x-7"
              >
                {series.map((item) => {
                  if (item.name === 'Revenue') {
                    return null;
                  }
                  return (
                    <div key={item.name} className="flex max-w-40 shrink-0">
                      <ChartLegendLabel item={item} />
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </AnalyticsCardState>
    </div>
  );
};

export default LeadStatusChartCard;
